/**
 * Payments Settings v2 API wrapper (DNKADM-0110).
 *
 * Registry-driven, provider-agnostic `/v2/admin/payments/**` surface. The client
 * keeps the wire names intact: provider, environment, currency, fields, webhook,
 * templates, and the `<key>Set` secret-presence flags are not renamed at the API
 * boundary. Reads are parsed defensively so unknown providers/fields keep
 * rendering instead of crashing the page.
 */
import { request } from './client';
import { ApiError } from './errors';

const PATH = '/v2/admin/payments';

type PaymentsConnectMode = 'oauth' | 'manual' | (string & {});
type PaymentsCredentialSource = 'oauth' | 'manual' | (string & {});
type PaymentsFieldType = 'text' | 'password' | 'select' | (string & {});
type PaymentsValidationStatus = 'unvalidated' | 'valid' | 'invalid';
type PaymentsConnectionOverall =
  | 'unconfigured'
  | 'not_activated'
  | 'healthy'
  | 'degraded'
  | 'down'
  | (string & {});

interface PaymentsFieldOption {
  value: string;
  label: string;
}

export interface PaymentsFieldDescriptor {
  key: string;
  type: PaymentsFieldType;
  label: string;
  helpText: string | null;
  options: PaymentsFieldOption[];
  validationRegex: string | null;
  required: boolean;
}

export interface PaymentsProviderRegistry {
  provider: string;
  label: string;
  enabled: boolean;
  connectModes: PaymentsConnectMode[];
  descriptors: PaymentsFieldDescriptor[];
  connectionFields: PaymentsFieldDescriptor[];
  webhookFields: PaymentsFieldDescriptor[];
  multiCurrency: boolean;
  allowedCurrencyOptions: string[];
  currencyBandFields: PaymentsFieldDescriptor[];
  overrideFields: PaymentsFieldDescriptor[];
}

export interface PaymentsRegistry {
  providers: PaymentsProviderRegistry[];
}

interface PaymentsConnectionTemplate {
  provider: string;
  environment: string | null;
  notificationUrl: string | null;
  webhook?: { notificationUrl?: string | null };
}

type PaymentsTemplateWebhook = { notificationUrl?: string | null };

interface PaymentsWebhookDescriptor {
  notificationUrl: string | null;
  secretSet: boolean;
  [key: string]: unknown;
}

export interface PaymentsCurrencyBand {
  currency: string;
  minChargeMinor: number | null;
  maxChargeMinor: number | null;
}

export interface PaymentsConnectionDescriptor {
  configured: boolean;
  templates: PaymentsConnectionTemplate[];
  provider: string | null;
  environment: string | null;
  environmentPermitted: boolean | null;
  currency: string | null;
  currencies: PaymentsCurrencyBand[];
  overridePinSet: boolean;
  fields: Record<string, string>;
  validationStatus: PaymentsValidationStatus;
  detail: string | null;
  active: boolean;
  locationSelectionRequired: boolean;
  credentialSource: PaymentsCredentialSource | null;
  accessTokenSet: boolean;
  oauthTokenExpiresAt: string | null;
  lastValidatedAt: string | null;
  connectedAccountName: string | null;
  webhook: PaymentsWebhookDescriptor;
  [key: string]: unknown;
}

export interface PaymentsConnectionInput {
  provider?: string;
  environment?: string;
  currency?: string;
  currencies?: PaymentsCurrencyBand[];
  overridePin?: string;
  fields?: Record<string, string>;
  webhook?: Record<string, string>;
  active?: boolean;
}

export interface PaymentsDisconnectOAuthResult {
  disconnected: boolean;
  pspRevoked: boolean;
}

export type PaymentsConvertToManualResult = PaymentsConnectionDescriptor;

export interface PaymentsValidationResult {
  validationStatus: PaymentsValidationStatus;
  detail: string | null;
}

/** Square's Apple Pay verdict for one site (DNKAPI-0597). */
export type PaymentsApplePayStatus = 'VERIFIED' | 'PENDING' | 'FAILED';

/** One site's row of the `POST /connection/apple-pay` result (DNKAPI-0597). */
export interface PaymentsApplePayDomain {
  domainName: string;
  status: PaymentsApplePayStatus;
  code: string | null;
  /** Plain-language reason, present on a FAILED site. */
  message: string | null;
}

export interface PaymentsStatus {
  connectionStatus: {
    overall: PaymentsConnectionOverall;
    [key: string]: unknown;
  };
}

export interface PaymentsConnectSessionInput {
  locationRef?: string;
  provider: string;
  environment: string;
  returnUrl: string;
}

export interface PaymentsConnectSession {
  connectUrl: string;
  expiresAt: string | null;
}

export interface PaymentsPspLocation {
  id: string;
  name: string;
  status: string | null;
}

type PaymentsWebhookVerdict =
  | 'healthy'
  | 'no_traffic'
  | 'attention'
  | 'misconfigured'
  | 'unconfigured'
  | (string & {});

interface PaymentsWebhookProviderSide {
  checked: boolean;
  subscriptionFound: boolean;
  matchesExpected: boolean;
  status: string | null;
  url: string | null;
  eventTypes: string[];
  note: string | null;
}

interface PaymentsWebhookInbound {
  signatureFailures7d: number | null;
  lastSignatureFailureAt: string | null;
  oldestUnresolvedFailureAt: string | null;
}

export type PaymentsWebhookStatus =
  | { available: false }
  | {
      available: true;
      verdict: PaymentsWebhookVerdict;
      secretSet: boolean;
      notificationUrl: string | null;
      providerSide: PaymentsWebhookProviderSide | null;
      inbound: PaymentsWebhookInbound | null;
      stats: Record<string, unknown>;
    };

interface PaymentsAttentionItem {
  type: string;
  severity: string;
  refs: string[];
  since: string | null;
  detail: string | null;
  locationRef: string | null;
}

interface PaymentsAttentionCounts {
  byType: Record<string, number>;
  bySeverity: Record<string, number>;
}

export type PaymentsAttention =
  | { available: false }
  | {
      available: true;
      counts: PaymentsAttentionCounts;
      items: PaymentsAttentionItem[];
    };

export type PaymentsWebhookTestResult =
  | { available: false }
  | {
      available: true;
      kind: 'roundTrip';
      sent: boolean;
      received: boolean;
      signatureOk: boolean;
      latencyMs: number | null;
    }
  | {
      available: true;
      kind: 'instructions';
      supported: false;
      reason: string | null;
      instructions: string;
    }
  | {
      available: true;
      kind: 'detail';
      supported: false;
      reason: string | null;
      detail: string;
    }
  | {
      available: true;
      kind: 'unsupported';
      supported: false;
      reason: string | null;
      detail: string | null;
    };

/**
 * How a location's charges are served (connections-overview, DNKAPI-0248 / handoff §2):
 * `self` = its own connection; `tenant_default` = it falls back to the tenant-default;
 * `none` = neither its own nor a chargeable default can serve it (the one actionable
 * "needs reconnect" state). Unknown strings are tolerated and treated calmly by the UI.
 */
type PaymentsServedBy = 'self' | 'tenant_default' | 'none' | (string & {});

/** Stored (non-live) health of a connection row (same vocabulary for the rollup default). */
export type PaymentsStoredHealth =
  | 'healthy_stored'
  | 'not_activated'
  | 'down'
  | 'unconfigured'
  | (string & {});

/**
 * One row of the connections overview — the tenant-default row (`isDefault:true`,
 * `locationRef:null`) or a per-location connection row (`isDefault:false`,
 * `locationRef:<v2_location.id>`). Stored-state only; no live ping.
 */
export interface PaymentsConnectionOverviewRow {
  isDefault: boolean;
  locationRef: string | null;
  servedBy: PaymentsServedBy;
  storedHealth: PaymentsStoredHealth;
  disconnected: boolean;
  active: boolean;
  provider: string | null;
  connectedAccountName: string | null;
}

/**
 * The `GET /v2/admin/payments/connections/overview` payload (DNKAPI-0248): the tenant
 * rollup plus one row per connection that exists. Parsed defensively; a location that
 * never had its own connection has no row and is composed against the location roster.
 */
export interface PaymentsConnectionsOverview {
  paymentsReady: boolean;
  activeCount: number;
  defaultStoredHealth: PaymentsStoredHealth;
  connections: PaymentsConnectionOverviewRow[];
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asText(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length === 0 ? null : trimmed;
  }
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function asBool(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function asNullableBool(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function asValidationStatus(value: unknown): PaymentsValidationStatus {
  return value === 'valid' || value === 'invalid' ? value : 'unvalidated';
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function parseOptions(raw: unknown): PaymentsFieldOption[] {
  return asArray(raw)
    .map((entry): PaymentsFieldOption | null => {
      // The backend sends select options as bare strings, e.g.
      // ["sandbox", "production"] (GET /v2/admin/payments/providers). Tolerate
      // the { value, label } object shape too, for forward-compatibility.
      if (typeof entry === 'string') {
        return entry.length > 0 ? { value: entry, label: entry } : null;
      }
      if (isObject(entry)) {
        const value = asText(entry['value']);
        if (value === null) return null;
        return { value, label: asText(entry['label']) ?? value };
      }
      return null;
    })
    .filter((entry): entry is PaymentsFieldOption => entry !== null);
}

function parseField(raw: unknown): PaymentsFieldDescriptor | null {
  if (!isObject(raw)) return null;
  const key = asText(raw['key']);
  if (key === null) return null;
  const type = asText(raw['type']) ?? 'text';
  return {
    key,
    type: type as PaymentsFieldType,
    label: asText(raw['label']) ?? key,
    helpText: asText(raw['helpText']),
    options: parseOptions(raw['options']),
    validationRegex: asText(raw['validationRegex']),
    required: asBool(raw['required']),
  };
}

function parseFields(raw: unknown): PaymentsFieldDescriptor[] {
  return asArray(raw)
    .map(parseField)
    .filter((field): field is PaymentsFieldDescriptor => field !== null);
}

function parseProvider(raw: unknown): PaymentsProviderRegistry | null {
  if (!isObject(raw)) return null;
  const provider = asText(raw['provider'] ?? raw['id']);
  if (provider === null) return null;
  const connectionFields = parseFields(raw['connectionFields']);
  const overrideFields = parseFields(raw['overrideFields']);
  return {
    provider,
    label: asText(raw['label'] ?? raw['name'] ?? raw['displayName']) ?? provider,
    enabled: asBool(raw['enabled'], true),
    connectModes: asArray(raw['connectModes'])
      .map(asText)
      .filter((mode): mode is PaymentsConnectMode => mode !== null),
    descriptors: parseFields(raw['descriptors'] ?? raw['fields'] ?? raw['credentialFields']),
    connectionFields,
    webhookFields: parseFields(raw['webhookFields']),
    multiCurrency: asBool(raw['multiCurrency']),
    allowedCurrencyOptions: asArray(raw['allowedCurrencyOptions'])
      .map(asText)
      .filter((currency): currency is string => currency !== null),
    currencyBandFields: parseFields(raw['currencyBandFields']),
    overrideFields,
  };
}

function parseRegistry(raw: unknown): PaymentsRegistry {
  const providers = isObject(raw) ? raw['providers'] : raw;
  return {
    providers: asArray(providers)
      .map(parseProvider)
      .filter((provider): provider is PaymentsProviderRegistry => provider !== null),
  };
}

function parseTemplates(raw: unknown): PaymentsConnectionTemplate[] {
  if (Array.isArray(raw)) {
    return raw
      .map((entry) => {
        if (!isObject(entry)) return null;
        const provider = asText(entry['provider'] ?? entry['id']);
        if (provider === null) return null;
        const nestedWebhook = isObject(entry['webhook']) ? entry['webhook'] : {};
        const template: PaymentsConnectionTemplate = {
          provider,
          environment: asText(entry['environment']),
          notificationUrl: asText(entry['notificationUrl'] ?? nestedWebhook['notificationUrl']),
        };
        if (isObject(entry['webhook'])) {
          template.webhook = entry['webhook'] as PaymentsTemplateWebhook;
        }
        return template;
      })
      .filter((entry): entry is PaymentsConnectionTemplate => entry !== null);
  }
  if (!isObject(raw)) return [];
  return Object.entries(raw)
    .map(([provider, entry]) => {
      const o = isObject(entry) ? entry : {};
      const nestedWebhook = isObject(o['webhook']) ? o['webhook'] : {};
      const template: PaymentsConnectionTemplate = {
        provider,
        environment: asText(o['environment']),
        notificationUrl: asText(o['notificationUrl'] ?? nestedWebhook['notificationUrl']),
      };
      if (isObject(o['webhook'])) {
        template.webhook = o['webhook'] as PaymentsTemplateWebhook;
      }
      return template;
    });
}

function parseStringRecord(raw: unknown): Record<string, string> {
  if (!isObject(raw)) return {};
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw)) {
    const text = asText(value);
    if (text !== null) out[key] = text;
  }
  return out;
}

function parseWebhook(raw: unknown): PaymentsWebhookDescriptor {
  const o = isObject(raw) ? raw : {};
  return {
    ...o,
    notificationUrl: asText(o['notificationUrl']),
    secretSet: asBool(o['secretSet']),
  };
}

function parseCurrencyBand(raw: unknown): PaymentsCurrencyBand | null {
  if (!isObject(raw)) return null;
  const currency = asText(raw['currency']);
  if (currency === null) return null;
  return {
    currency,
    minChargeMinor: asNumber(raw['minChargeMinor']),
    maxChargeMinor: asNumber(raw['maxChargeMinor']),
  };
}

function parseCurrencyBands(raw: unknown): PaymentsCurrencyBand[] {
  return asArray(raw)
    .map(parseCurrencyBand)
    .filter((band): band is PaymentsCurrencyBand => band !== null);
}

function parseDescriptor(raw: unknown): PaymentsConnectionDescriptor {
  const o = isObject(raw) ? raw : {};
  // DNKADM-0400: the PIN is write-only. Should a server still send it, it is dropped
  // here so the parsed descriptor never carries it — `overridePinSet` is the only PIN fact.
  const passthrough: Record<string, unknown> = { ...o };
  delete passthrough['overridePin'];
  return {
    ...passthrough,
    configured: asBool(o['configured']),
    templates: parseTemplates(o['templates']),
    provider: asText(o['provider']),
    environment: asText(o['environment']),
    environmentPermitted: asNullableBool(o['environmentPermitted']),
    currency: asText(o['currency']),
    currencies: parseCurrencyBands(o['currencies']),
    overridePinSet: asBool(o['overridePinSet']),
    fields: parseStringRecord(o['fields']),
    validationStatus: asValidationStatus(o['validationStatus']),
    detail: asText(o['detail'] ?? o['lastValidationDetail']),
    active: asBool(o['active']),
    locationSelectionRequired: asBool(o['locationSelectionRequired']),
    credentialSource: asText(o['credentialSource']) as PaymentsCredentialSource | null,
    accessTokenSet: asBool(o['accessTokenSet']),
    oauthTokenExpiresAt: asText(o['oauthTokenExpiresAt']),
    lastValidatedAt: asText(o['lastValidatedAt']),
    connectedAccountName: asText(o['connectedAccountName']),
    webhook: parseWebhook(o['webhook']),
  };
}

function parseDisconnectOAuth(raw: unknown): PaymentsDisconnectOAuthResult {
  const o = isObject(raw) ? raw : {};
  return {
    disconnected: asBool(o['disconnected']),
    pspRevoked: asBool(o['pspRevoked']),
  };
}

function parseValidation(raw: unknown): PaymentsValidationResult {
  const o = isObject(raw) ? raw : {};
  return {
    validationStatus: asValidationStatus(o['validationStatus']),
    detail: asText(o['detail']),
  };
}

/** An unknown status reads as FAILED (with whatever message came with it), never as a crash. */
function asApplePayStatus(value: unknown): PaymentsApplePayStatus {
  const status = asText(value)?.toUpperCase();
  return status === 'VERIFIED' || status === 'PENDING' ? status : 'FAILED';
}

function parseApplePayDomains(raw: unknown): PaymentsApplePayDomain[] {
  return asArray(raw).flatMap((entry): PaymentsApplePayDomain[] => {
    if (!isObject(entry)) return [];
    const domainName = asText(entry['domainName']);
    if (domainName === null) return [];
    return [
      {
        domainName,
        status: asApplePayStatus(entry['status']),
        code: asText(entry['code']),
        message: asText(entry['message']),
      },
    ];
  });
}

function parseStatus(raw: unknown): PaymentsStatus {
  const o = isObject(raw) ? raw : {};
  const connectionStatus = isObject(o['connectionStatus']) ? o['connectionStatus'] : {};
  return {
    connectionStatus: {
      ...connectionStatus,
      overall: (asText(connectionStatus['overall']) ?? 'unconfigured') as PaymentsConnectionOverall,
    },
  };
}

function parseConnectSession(raw: unknown): PaymentsConnectSession {
  const o = isObject(raw) ? raw : {};
  return {
    connectUrl: asText(o['connectUrl']) ?? '',
    expiresAt: asText(o['expiresAt']),
  };
}

function parsePspLocation(raw: unknown): PaymentsPspLocation | null {
  if (!isObject(raw)) return null;
  const id = asText(raw['id']);
  if (id === null) return null;
  return {
    id,
    name: asText(raw['name']) ?? id,
    status: asText(raw['status']),
  };
}

function parsePspLocations(raw: unknown): PaymentsPspLocation[] {
  const list = isObject(raw) && Array.isArray(raw['locations']) ? raw['locations'] : raw;
  return asArray(list)
    .map(parsePspLocation)
    .filter((loc): loc is PaymentsPspLocation => loc !== null);
}

function parseNumberRecord(raw: unknown): Record<string, number> {
  if (!isObject(raw)) return {};
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(raw)) {
    const number = asNumber(value);
    if (number !== null) out[key] = number;
  }
  return out;
}

function parseWebhookProviderSide(raw: unknown): PaymentsWebhookProviderSide | null {
  if (!isObject(raw)) return null;
  return {
    checked: asBool(raw['checked']),
    subscriptionFound: asBool(raw['subscriptionFound']),
    matchesExpected: asBool(raw['matchesExpected']),
    status: asText(raw['status']),
    url: asText(raw['url']),
    eventTypes: asArray(raw['eventTypes'])
      .map(asText)
      .filter((event): event is string => event !== null),
    note: asText(raw['note']),
  };
}

function parseWebhookInbound(raw: unknown): PaymentsWebhookInbound | null {
  if (!isObject(raw)) return null;
  const counts7d = isObject(raw['counts7d']) ? raw['counts7d'] : {};
  return {
    signatureFailures7d: asNumber(counts7d['signatureFailures']),
    lastSignatureFailureAt: asText(raw['lastSignatureFailureAt']),
    oldestUnresolvedFailureAt: asText(raw['oldestUnresolvedFailureAt']),
  };
}

function parseWebhookStatus(raw: unknown): PaymentsWebhookStatus {
  const o = isObject(raw) ? raw : {};
  const explicitStats = isObject(o['stats']) ? o['stats'] : null;
  const stats: Record<string, unknown> = {};
  const source = explicitStats ?? o;
  for (const [key, value] of Object.entries(source)) {
    if (key !== 'verdict' && value !== null && value !== undefined) stats[key] = value;
  }
  return {
    available: true,
    verdict: (asText(o['verdict']) ?? 'unconfigured') as PaymentsWebhookVerdict,
    secretSet: asBool(o['secretSet'], true),
    notificationUrl: asText(o['notificationUrl']),
    providerSide: parseWebhookProviderSide(o['providerSide']),
    inbound: parseWebhookInbound(o['inbound']),
    stats,
  };
}

type PaymentsWebhookReasonCode =
  | 'missing_secret'
  | 'not_registered'
  | 'url_events_mismatch'
  | 'not_checked';

export interface PaymentsWebhookReason {
  code: PaymentsWebhookReasonCode;
  /** Short, human one-liner for the summary tile. */
  text: string;
  /** Optional supporting detail (e.g. the URL/events comparison); null when none. */
  detail: string | null;
}

/**
 * Humanize a providerSide.note code into a short sentence. Unknown codes are
 * de-underscored/de-cased generically so new backend codes still read sensibly.
 */
function webhookProviderNoteText(note: string | null): string {
  switch (note) {
    case 'unreachable':
      return 'Provider unreachable — could not verify the subscription';
    case 'credential_absent':
      return 'No credentials available to check the provider';
    case 'customer_managed':
      return 'Subscription is managed outside this console';
    case null:
      return 'Provider subscription could not be checked';
    default:
      return note.replace(/[_-]+/g, ' ').trim();
  }
}

/**
 * The single source of truth for "a webhook subscription is missing at the
 * provider" — checked successfully but no subscription registered. The companion
 * Re-create subscription button gates on THIS exact predicate; keep it shared.
 */
export function isWebhookSubscriptionMissing(status: PaymentsWebhookStatus): boolean {
  if (!status.available) return false;
  const ps = status.providerSide;
  return ps !== null && ps.checked && !ps.subscriptionFound;
}

/**
 * Derive the one-line reason shown under the badge. Returns null unless the
 * verdict warrants attention (misconfigured | attention) AND a mapped condition
 * matches. Precedence is fixed and ordered:
 *   1. missing signing secret
 *   2. subscription not registered at the provider
 *   3. registered but URL/events don't match ours
 *   4. provider could not be checked (humanized note)
 */
export function webhookStatusReason(status: PaymentsWebhookStatus): PaymentsWebhookReason | null {
  if (!status.available) return null;
  if (status.verdict !== 'misconfigured' && status.verdict !== 'attention') return null;

  if (!status.secretSet) {
    return { code: 'missing_secret', text: 'Missing webhook signing secret', detail: null };
  }

  const ps = status.providerSide;
  if (ps !== null && ps.checked && !ps.subscriptionFound) {
    return { code: 'not_registered', text: 'Not registered at Square', detail: null };
  }

  if (ps !== null && ps.checked && ps.subscriptionFound && !ps.matchesExpected) {
    const configured = ps.url ?? '—';
    const events = ps.eventTypes.length > 0 ? ps.eventTypes.join(', ') : '—';
    const expected = status.notificationUrl ?? '—';
    return {
      code: 'url_events_mismatch',
      text: 'Webhook URL/events mismatch',
      detail: `Registered ${configured} [${events}] · expected ${expected}`,
    };
  }

  if (ps !== null && !ps.checked) {
    return { code: 'not_checked', text: webhookProviderNoteText(ps.note), detail: null };
  }

  return null;
}

function parseAttentionItem(raw: unknown): PaymentsAttentionItem | null {
  if (!isObject(raw)) return null;
  const type = asText(raw['type']);
  const severity = asText(raw['severity']);
  if (type === null || severity === null) return null;
  return {
    type,
    severity,
    refs: asArray(raw['refs'])
      .map(asText)
      .filter((ref): ref is string => ref !== null),
    since: asText(raw['since']),
    detail: asText(raw['detail']),
    locationRef: asText(raw['locationRef']),
  };
}

function parseAttention(raw: unknown): PaymentsAttention {
  const o = isObject(raw) ? raw : {};
  const counts = isObject(o['counts']) ? o['counts'] : {};
  return {
    available: true,
    counts: {
      byType: parseNumberRecord(counts['byType']),
      bySeverity: parseNumberRecord(counts['bySeverity']),
    },
    items: asArray(o['items'])
      .map(parseAttentionItem)
      .filter((item): item is PaymentsAttentionItem => item !== null),
  };
}

function parseWebhookTestResult(raw: unknown): PaymentsWebhookTestResult {
  const o = isObject(raw) ? raw : {};
  const supported = o['supported'];
  if (supported === false) {
    const reason = asText(o['reason']);
    const detail = asText(o['detail']);
    if (reason === 'no_webhooks') {
      if (detail !== null) {
        return { available: true, kind: 'detail', supported: false, reason, detail };
      }
      return { available: true, kind: 'unsupported', supported: false, reason, detail: null };
    }
    const instructions = asText(o['instructions']);
    if (instructions !== null) {
      return { available: true, kind: 'instructions', supported: false, reason, instructions };
    }
    return { available: true, kind: 'unsupported', supported: false, reason, detail };
  }
  return {
    available: true,
    kind: 'roundTrip',
    sent: asBool(o['sent']),
    received: asBool(o['received']),
    signatureOk: asBool(o['signatureOk']),
    latencyMs: asNumber(o['latencyMs']),
  };
}

function asStoredHealth(value: unknown): PaymentsStoredHealth {
  // Absent/non-string stored health degrades to 'unconfigured' (the calm neutral).
  return (asText(value) ?? 'unconfigured') as PaymentsStoredHealth;
}

function parseConnectionOverviewRow(raw: unknown): PaymentsConnectionOverviewRow | null {
  if (!isObject(raw)) return null;
  const isDefault = asBool(raw['isDefault']);
  const locationRef = asText(raw['locationRef']);
  // A malformed row that is neither the tenant-default nor tied to a location can't
  // be placed in either table, so it is dropped.
  if (!isDefault && locationRef === null) return null;
  return {
    isDefault,
    locationRef,
    // Absent/non-string servedBy degrades to the calm 'tenant_default' — unknown
    // states never render as alarms (composition rule's defensive default).
    servedBy: (asText(raw['servedBy']) ?? 'tenant_default') as PaymentsServedBy,
    storedHealth: asStoredHealth(raw['storedHealth']),
    disconnected: asBool(raw['disconnected']),
    active: asBool(raw['active']),
    provider: asText(raw['provider']),
    connectedAccountName: asText(raw['connectedAccountName']),
  };
}

function parseConnectionsOverview(raw: unknown): PaymentsConnectionsOverview {
  const o = isObject(raw) ? raw : {};
  // Envelope tolerance (mirrors parsePspLocations): the connections array under an
  // object body, or a bare array body.
  const connections = isObject(raw) && Array.isArray(raw['connections']) ? raw['connections'] : raw;
  return {
    paymentsReady: asBool(o['paymentsReady']),
    activeCount: asNumber(o['activeCount']) ?? 0,
    defaultStoredHealth: asStoredHealth(o['defaultStoredHealth']),
    connections: asArray(connections)
      .map(parseConnectionOverviewRow)
      .filter((row): row is PaymentsConnectionOverviewRow => row !== null),
  };
}

function unavailableOn404<T extends { available: false }>(
  err: unknown,
  fallback: T,
): T {
  if (err instanceof ApiError && (err.status === 404 || err.code === 'NOT_FOUND')) return fallback;
  throw err;
}

function locationQuery(locationRef?: string): { locationRef?: string } | undefined {
  if (locationRef === undefined) return undefined;
  return { locationRef };
}

function optionsWithLocation(locationRef?: string): { query?: { locationRef?: string } } {
  const options: { query?: { locationRef?: string } } = {};
  const query = locationQuery(locationRef);
  if (query !== undefined) options.query = query;
  return options;
}

export async function getPaymentsRegistry(): Promise<PaymentsRegistry> {
  return parseRegistry(await request<unknown>('GET', `${PATH}/providers`));
}

/**
 * `GET /v2/admin/payments/connections/overview` (DNKAPI-0248) → the stored-state
 * connections overview (rollup + one row per connection). No `unavailableOn404`
 * fallback — the endpoint is shipped; a failure surfaces the tab's error/retry state.
 */
export async function getPaymentsConnectionsOverview(): Promise<PaymentsConnectionsOverview> {
  return parseConnectionsOverview(
    await request<unknown>('GET', `${PATH}/connections/overview`),
  );
}

export async function getPaymentsConnection(
  locationRef?: string,
): Promise<PaymentsConnectionDescriptor> {
  return parseDescriptor(
    await request<unknown>('GET', `${PATH}/connection`, optionsWithLocation(locationRef)),
  );
}

export async function putPaymentsConnection(
  locationRef: string | undefined,
  body: PaymentsConnectionInput,
): Promise<PaymentsConnectionDescriptor> {
  return parseDescriptor(
    await request<unknown>('PUT', `${PATH}/connection`, {
      ...optionsWithLocation(locationRef),
      json: body,
    }),
  );
}

export async function convertPaymentsConnectionToManual(
  locationRef: string | undefined,
  body: PaymentsConnectionInput,
): Promise<PaymentsConvertToManualResult> {
  return parseDescriptor(
    await request<unknown>('POST', `${PATH}/connection/convert-to-manual`, {
      ...optionsWithLocation(locationRef),
      json: body,
    }),
  );
}

export async function disconnectPaymentsOAuth(
  locationRef?: string,
): Promise<PaymentsDisconnectOAuthResult> {
  return parseDisconnectOAuth(
    await request<unknown>('DELETE', `${PATH}/connection/oauth-grant`, {
      ...optionsWithLocation(locationRef),
    }),
  );
}

export async function deletePaymentsConnection(locationRef?: string): Promise<void> {
  await request<unknown>('DELETE', `${PATH}/connection`, optionsWithLocation(locationRef));
}

export async function validatePaymentsConnection(
  locationRef?: string,
): Promise<PaymentsValidationResult> {
  return parseValidation(
    await request<unknown>('POST', `${PATH}/connection/validate`, {
      ...optionsWithLocation(locationRef),
    }),
  );
}

export async function getPaymentsStatus(locationRef?: string): Promise<PaymentsStatus> {
  return parseStatus(
    await request<unknown>('GET', `${PATH}/connection/status`, {
      ...optionsWithLocation(locationRef),
    }),
  );
}

/**
 * `POST /connection/apple-pay` (DNKAPI-0597) → registers this connection's sites for Apple Pay with
 * Square and returns each site's verdict. Safe to repeat: a second call re-checks the same sites.
 */
export async function setUpApplePay(locationRef?: string): Promise<PaymentsApplePayDomain[]> {
  return parseApplePayDomains(
    await request<unknown>('POST', `${PATH}/connection/apple-pay`, {
      ...optionsWithLocation(locationRef),
    }),
  );
}

export async function startConnectSession(
  input: PaymentsConnectSessionInput,
): Promise<PaymentsConnectSession> {
  const json: Record<string, string> = {
    provider: input.provider,
    environment: input.environment,
    returnUrl: input.returnUrl,
  };
  if (input.locationRef !== undefined) json['locationRef'] = input.locationRef;
  return parseConnectSession(
    await request<unknown>('POST', `${PATH}/connect-session`, { json }),
  );
}

export async function getPspLocations(locationRef?: string): Promise<PaymentsPspLocation[]> {
  return parsePspLocations(
    await request<unknown>('GET', `${PATH}/connection/psp-location`, {
      ...optionsWithLocation(locationRef),
    }),
  );
}

export async function setPspLocation(
  locationRef: string | undefined,
  squareLocationId: string,
): Promise<void> {
  await request<unknown>('PUT', `${PATH}/connection/psp-location`, {
    ...optionsWithLocation(locationRef),
    json: { squareLocationId },
  });
}

export async function getWebhookStatus(locationRef?: string): Promise<PaymentsWebhookStatus> {
  try {
    return parseWebhookStatus(
      await request<unknown>('GET', `${PATH}/webhooks/status`, {
        ...optionsWithLocation(locationRef),
      }),
    );
  } catch (err) {
    return unavailableOn404(err, { available: false });
  }
}

export async function getPaymentsAttention(): Promise<PaymentsAttention> {
  try {
    return parseAttention(await request<unknown>('GET', `${PATH}/attention`));
  } catch (err) {
    return unavailableOn404(err, { available: false });
  }
}

export async function sendWebhookTest(locationRef?: string): Promise<PaymentsWebhookTestResult> {
  try {
    return parseWebhookTestResult(
      await request<unknown>('POST', `${PATH}/webhooks/test`, {
        ...optionsWithLocation(locationRef),
      }),
    );
  } catch (err) {
    return unavailableOn404(err, { available: false });
  }
}
